// Reception dashboard — shared vocabulary (RON-5469).
//
// Backend contract (READ-ONLY, the Laravel team's folder — never write to it):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
// Section references (§3, §4 …) below point at that document.
//
// The prototype this mirrors:
//   D:\laragon\www\visitor-management-prototype_updated_2\prototypes\reception-dashboard.html

// ── The seven filters (§3) ───────────────────────────────────────────────────────────────────
//
// "Anything else is a 422 listing all seven, not an empty list — '0 expected' would read to a
// receptionist as a fact about their day."
//
// `all` is a REAL value, not the absence of one, so it is never special-cased away before the
// request is built.
export const RECEPTION_FILTER = {
  ALL: "all",
  EXPECTED: "expected",
  ON_SITE: "on_site",
  WALK_INS: "walk_ins",
  VIP: "vip",
  CONTRACTORS: "contractors",
  OVERSTAY: "overstay",
};

export const RECEPTION_FILTERS = Object.values(RECEPTION_FILTER);

// Tab order is the prototype's, left to right.
export const FILTER_TAB_ORDER = [
  RECEPTION_FILTER.ALL,
  RECEPTION_FILTER.EXPECTED,
  RECEPTION_FILTER.ON_SITE,
  RECEPTION_FILTER.WALK_INS,
  RECEPTION_FILTER.VIP,
  RECEPTION_FILTER.CONTRACTORS,
  RECEPTION_FILTER.OVERSTAY,
];

const FILTER_LABEL_KEYS = {
  [RECEPTION_FILTER.ALL]: "react_reception_filter_all",
  [RECEPTION_FILTER.EXPECTED]: "react_reception_filter_expected",
  [RECEPTION_FILTER.ON_SITE]: "react_reception_filter_on_site",
  [RECEPTION_FILTER.WALK_INS]: "react_reception_filter_walk_ins",
  [RECEPTION_FILTER.VIP]: "react_reception_filter_vip",
  [RECEPTION_FILTER.CONTRACTORS]: "react_reception_filter_contractors",
  [RECEPTION_FILTER.OVERSTAY]: "react_reception_filter_overstay",
};

export const getFilterLabelKey = (filter) =>
  FILTER_LABEL_KEYS[filter] || FILTER_LABEL_KEYS[RECEPTION_FILTER.ALL];

// ── The five stat tiles (§3) ─────────────────────────────────────────────────────────────────
//
// THE TILES AND THE TABS READ THE SAME `counts` OBJECT. The API returns all seven counts once,
// precisely so the two regions cannot disagree — five of them are also drawn as tiles. Nothing
// here recomputes a count from the rows on screen.
//
// "They describe the whole day and site, regardless of `filter` and `q`. A tab badge answers
//  'how many are behind this tab' — one that shrank as you typed would be answering a different
//  question. Only `items` narrows."
// `icon` is the prototype's glyph for each tile. Kept as characters rather than SVGs because they
// are decorative marks beside a label that already says what the number is — an icon font or five
// more files would be weight for nothing.
export const STAT_TILES = [
  { key: "onSite", countKey: "onSite", tone: "onsite", icon: "●", labelKey: "react_reception_stat_on_site" },
  { key: "expected", countKey: "expected", tone: "expected", icon: "⏱", labelKey: "react_reception_stat_expected" },
  { key: "walkIns", countKey: "walkIns", tone: "walkins", icon: "+", labelKey: "react_reception_stat_walk_ins" },
  { key: "vip", countKey: "vip", tone: "vip", icon: "★", labelKey: "react_reception_stat_vip" },
  { key: "overstay", countKey: "overstay", tone: "overstay", icon: "!", labelKey: "react_reception_stat_overstay" },
];

// ── The tiles change TENSE with the date ─────────────────────────────────────────────────────
//
// A board showing yesterday with "ON SITE 0" is a statement that the building is empty. It is not:
// nobody is on site on a day that has finished. The prototype rewrites the labels, and it is right
// to — "Were on site" is the only reading of that number that is true.
//
// Only three of the five move. "Walk-ins" and "VIPs" are counts of things that happened, and read
// correctly in any tense.
export const RELATIVE_DAY = { PAST: "past", TODAY: "today", FUTURE: "future" };

const TENSE_LABEL_KEYS = {
  onSite: {
    past: "react_reception_stat_on_site_past",
    future: "react_reception_stat_on_site_future",
  },
  expected: { past: "react_reception_stat_expected_past" },
  overstay: { past: "react_reception_stat_overstay_past" },
};

export const getStatLabelKey = (tile, relativeDay) =>
  TENSE_LABEL_KEYS[tile.key]?.[relativeDay] || tile.labelKey;

// Past, today or future — WITHOUT comparing clocks with the server.
//
// `is_today` is authoritative and comes from the API (§3), so it settles the "today" case on its
// own. The only thing left to decide is which side of today the other dates fall, and that is
// answered by comparing against whichever date the server last told us was today — never against
// the browser's clock, which can be a day out and is a different machine's opinion anyway.
export const getRelativeDay = (dateISO, isToday, serverTodayISO) => {
  if (isToday) return RELATIVE_DAY.TODAY;
  if (!dateISO || !serverTodayISO) return RELATIVE_DAY.TODAY;

  return String(dateISO) < String(serverTodayISO) ? RELATIVE_DAY.PAST : RELATIVE_DAY.FUTURE;
};

// How fresh the board is, in words. The prototype's footer reads "Updated 2 seconds ago", and on a
// screen that polls, "how long ago" is the question — a raw timestamp makes the reader subtract.
//
// SETTLED 14-October-2026, and the answer removed the question rather than answering it.
//
// This used to carry a warning: it assumed `as_at` was UTC, the contract only ever called it
// "the server's clock at the read", and one of those readings had to be wrong by an hour without
// us knowing which.
//
// The `_local` twins (API-STATUS §0) settle it. The reception board is a covered response, so
// `as_at_local` arrives already in the site's own zone and `receptionApiMapper` reads that.
// There is no conversion left to get wrong — which is why this function no longer needs to know
// what zone anything is in.
//
// The "Z" below still forces UTC for the FALLBACK case, where a response predates the twins and
// only the raw `as_at` exists. That path is now the exception rather than the rule.
// How long ago, from a DURATION WE MEASURED OURSELVES. No clock comparison, so no timezone.
//
// THIS IS WHAT THE FOOTER USES, and it stays that way even now the twins have settled the zone.
//
// It was written because the footer was resting its whole output on an unverified claim that
// `as_at` was UTC — and if that was an hour out, the footer would read "60 minutes ago" the
// instant the board refreshed, on the one element whose entire job is to say how fresh the board
// is, on a screen that doubles as an evacuation muster.
//
// `as_at_local` would now make a clock comparison safe. This is still better, because it answers
// a different and more useful question: not "what time does the server think it is" but "how long
// since THIS SCREEN last heard anything". A board that has silently stopped polling shows its age
// here regardless of what the server's clock says.
export const relativeFromMs = (elapsedMs) => {
  if (!Number.isFinite(elapsedMs)) return null;

  const seconds = Math.round(elapsedMs / 1000);

  if (seconds < 45) return { key: "react_reception_just_now", value: null };
  if (seconds < 3600) return { key: "react_reception_mins_ago", value: Math.round(seconds / 60) };
  if (seconds < 86400) return { key: "react_reception_hours_ago", value: Math.round(seconds / 3600) };

  return { key: "react_reception_days_ago", value: Math.round(seconds / 86400) };
};

export const formatRelativeTime = (asAt, now = Date.now()) => {
  if (!asAt) return null;

  const parsed = Date.parse(`${String(asAt).trim().replace(" ", "T")}Z`);
  if (Number.isNaN(parsed)) return null;

  const seconds = Math.round((now - parsed) / 1000);

  // A clock a little ahead of the server reads as the future; "just now" is truer than "in 3
  // seconds" and does not make anybody doubt the screen.
  if (seconds < 45) return { key: "react_reception_just_now", value: null };
  if (seconds < 3600) return { key: "react_reception_mins_ago", value: Math.round(seconds / 60) };
  if (seconds < 86400) return { key: "react_reception_hours_ago", value: Math.round(seconds / 3600) };

  return { key: "react_reception_days_ago", value: Math.round(seconds / 86400) };
};

// "Michael Walsh" → "M. Walsh". The host column is narrow and the surname is what a receptionist
// matches on; the full name is on the detail panel, where there is room for it.
//
// A single-word name is left alone — "M." on its own identifies nobody.
export const abbreviateName = (name) => {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);

  if (parts.length < 2) return parts[0] || "";

  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

// The prototype's tiles carry trend chips ("12% up", "vs avg"). §9: there is still no source for a
// rolling per-site average, so they are deliberately NOT rendered. An invented trend on a
// reception screen is a number somebody will repeat in a meeting.

// ── Row status (§4) ──────────────────────────────────────────────────────────────────────────
//
// `cancelled` and `no_show` are not on the board at all, so they are absent here by design.
export const ROW_STATUS = {
  EXPECTED: "expected",
  CHECKED_IN: "checked_in",
  CHECKED_OUT: "checked_out",
};

const STATUS_LABEL_KEYS = {
  [ROW_STATUS.EXPECTED]: "react_reception_status_expected",
  [ROW_STATUS.CHECKED_IN]: "react_reception_status_checked_in",
  [ROW_STATUS.CHECKED_OUT]: "react_reception_status_checked_out",
};

export const getStatusLabelKey = (status) =>
  STATUS_LABEL_KEYS[status] || "react_reception_status_expected";

// OVERSTAY IS A FLAG, NEVER A STATUS (§4).
//
// "Overstays stay on the board and on the muster, flagged, never auto-cleared — because dropping
//  a still-present person from an evacuation list is unsafe."
//
// So a row can be `checked_in` AND overstaying, and the badge has to say both. Treating overstay
// as a fourth status would drop the person off the "on site" count, which is the count a fire
// warden acts on.
export const isOverstaying = (row) => row?.isOverstay === true;

// Minutes → "2H 18M", the prototype's format. A receptionist reads a duration, not a count: "74
// minutes" needs converting in your head before it means anything, and the number that matters
// ("over two hours") is the part that has to land first.
//
// Under an hour it stays in minutes, because "0H 42M" is worse than "42M".
export const formatOverstay = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));

  if (total < 60) return `${total}M`;

  return `${Math.floor(total / 60)}H ${total % 60}M`;
};

// Which pill a row earns. VIP wins over the plain status because it is what the desk needs to see
// first; overstay wins over everything, because it is the only one that needs acting on.
export const getRowTone = (row) => {
  if (!row) return "expected";
  if (isOverstaying(row)) return "overstay";
  if (row.isVip) return "vip";
  if (row.status === ROW_STATUS.CHECKED_IN) return "onsite";
  if (row.status === ROW_STATUS.CHECKED_OUT) return "signedout";

  return "expected";
};

// ── Paging (§3) ──────────────────────────────────────────────────────────────────────────────
//
// `per_page` defaults to 200 HERE, not to the module's usual 25, and 200 is also the maximum.
// "Send no per_page and you get exactly the board you got before paging existed."
//
// A bad value is a 422, never a silent correction: `page=0` and `per_page=1000` are both refused
// rather than quietly becoming 1 and 200.
export const DEFAULT_PER_PAGE = 200;
export const MAX_PER_PAGE = 200;

// ── Auto-refresh (§9) ────────────────────────────────────────────────────────────────────────
//
// The board has no push channel and no delta endpoint, so it polls. The number is the backend's,
// not a guess:
//
//   "60 seconds, switch it on, do not wait for us. The board is a handful of queries with the
//    seven counts already aggregated into one. Nine hours at one tab per desk is roughly 540
//    requests per desk per day, which is not a number that worries anyone here. 60 rather than
//    30, by your own reasoning: you said a receptionist notices staleness in about a minute, so
//    30 doubles the load for something nobody perceives."
//
// JITTER WAS THEIR ONE ASK BACK: "twenty desks that all open at nine o'clock will otherwise poll
// in lockstep for the rest of the day." A fixed interval synchronises every desk that started at
// the same time — the morning shift — and keeps them synchronised, so the server sees a spike a
// minute instead of a trickle.
//
// Spread ADDED to the interval rather than centred on it, so a desk never polls faster than the
// 60 seconds they sized the load against.
export const BOARD_POLL_MS = 60000;
export const BOARD_POLL_JITTER_MS = 15000;

// Each tick waits 60–75s. Exported so a test can assert the window rather than re-deriving it.
export const nextPollDelay = (random = Math.random) =>
  BOARD_POLL_MS + Math.floor(random() * BOARD_POLL_JITTER_MS);

// ── Compliance (§4) ──────────────────────────────────────────────────────────────────────────
//
// FOUR LINES, AND ONLY TWO ARE REAL.
//
// `photo_captured` and the badge pair come from the kiosk. `hs_acknowledged` and `nda_signed` are
// ABSENT, not `false` — nothing writes them yet:
//
//   "`false` would say 'we asked and they refused', which is a different fact about a person
//    standing at a desk, and a red cross on your panel would state it as truth."
//
// So the panel must distinguish three states, not two. `NOT_RECORDED` is why this returns a
// string rather than a boolean.
export const COMPLIANCE_STATE = {
  YES: "yes",
  NO: "no",
  NOT_RECORDED: "not_recorded",
};

export const getComplianceState = (compliance, key) => {
  if (!compliance || !(key in compliance)) return COMPLIANCE_STATE.NOT_RECORDED;

  return compliance[key] ? COMPLIANCE_STATE.YES : COMPLIANCE_STATE.NO;
};

// A walk-in's `require_hs` / `require_nda` switches are NOT these (§5). They record what the desk
// said was required, not what anybody signed, and must never render as a tick here.

// ── Initials for the avatar chip ─────────────────────────────────────────────────────────────
//
// `String(null)` is `"null"`, so a null name would produce the initial "N". Guarded with `?? ""`
// rather than a default parameter, which only covers `undefined`.
export const getInitials = (name) =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// Avatar colour, chosen from the name so the same person is the same colour on every render
// without the API having to carry one.
const AVATAR_TONES = ["blue", "teal", "amber", "coral", "violet"];

// A VIP is ALWAYS amber, whatever their name hashes to — the prototype's `av amber vip`.
// Consistency matters more than variety here: the colour is a signal, not decoration, and a VIP
// who came out teal would be the one nobody spotted.
export const getRowAvatarTone = (row) =>
  row?.isVip ? "amber" : getAvatarTone(row?.name);

export const getAvatarTone = (name) => {
  const text = String(name ?? "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash + text.charCodeAt(index)) % AVATAR_TONES.length;
  }

  return AVATAR_TONES[hash];
};

// ── Count-sensitive copy ─────────────────────────────────────────────────────────────────────
// This app interpolates with `.replace("%s", n)` and has no plural machinery, so "1 visitors"
// needs a `_one` sibling key. Same helper as the Visitor module.
export const pluralKey = (key, count) => (Number(count) === 1 ? `${key}_one` : key);

export const pluralText = (t, key, count) =>
  t(pluralKey(key, count)).replace("%s", String(count));

// ── Un-vetted contractor approval — Phase H (answers-to-frontend-17 and -18) ─────────────────
//
// The four reason codes, THEIRS. Ours were `host_accepted`, `verified_in_person`,
// `urgent_works`, `other` — two of the four were wrong, and the rework of 22-October replaced
// them. We were right that a code should travel rather than an English sentence (an audit trail
// of free text cannot be filtered or counted, and this app ships in nine locales); we were wrong
// about two of the words.
//
// The labels live in the kiosk config (`approval_reasons`) server-side and match the prototype.
// We render our own translations of the same four so the dropdown works in every locale.
export const APPROVAL_REASON = {
  HOST_RESPONSIBILITY: "host_responsibility",
  VERIFIED_IN_PERSON: "verified_in_person",
  EMERGENCY_WORKS: "emergency_works",
  OTHER: "other",
};

// Order matters: the prototype's dropdown order, strongest justification first.
export const APPROVAL_REASONS = [
  APPROVAL_REASON.HOST_RESPONSIBILITY,
  APPROVAL_REASON.VERIFIED_IN_PERSON,
  APPROVAL_REASON.EMERGENCY_WORKS,
  APPROVAL_REASON.OTHER,
];

const APPROVAL_REASON_LABEL_KEYS = {
  [APPROVAL_REASON.HOST_RESPONSIBILITY]: "react_reception_approval_reason_host_accepted",
  [APPROVAL_REASON.VERIFIED_IN_PERSON]: "react_reception_approval_reason_verified",
  [APPROVAL_REASON.EMERGENCY_WORKS]: "react_reception_approval_reason_urgent",
  [APPROVAL_REASON.OTHER]: "react_reception_approval_reason_other",
};

// Falls back to the raw code rather than an empty string: an unknown reason arriving from a
// newer server should read as something a person can report, not as a blank line.
export const getApprovalReasonLabelKey = (reason) =>
  APPROVAL_REASON_LABEL_KEYS[reason] ?? reason ?? "";

// `other` REQUIRES a note. Their endpoint 422s without one.
//
// We had no note field at all, so until 22-October a receptionist who chose "Other" could press
// Approve and would have been refused by the server with nothing on screen explaining why.
export const approvalReasonNeedsNote = (reason) => reason === APPROVAL_REASON.OTHER;

// "Aoife Ryan (Ryan Facilities)" for the banner, "Aoife Ryan · Ryan Facilities" for the dialog
// heading — the prototype uses each in its own place.
//
// Here rather than inline in the components because the shell's tests render translation keys
// rather than translated text, so anything composed inside a `t()` call is invisible to them.
// A contractor with no company on file must not produce "Aoife Ryan ()".
export const formatApprovalSubject = (pending, { brackets = false } = {}) => {
  const name = pending?.name ?? "";
  const company = pending?.company;

  if (!company) return name;

  return brackets ? `${name} (${company})` : `${name} · ${company}`;
};

// Approve is enabled only when EVERY outstanding item is ticked, a reason is chosen, and — when
// the reason is `other` — a note has been typed.
//
// Kept here rather than in the component so the rule is unit-testable without rendering a
// dialog, and so the dialog and the mock API enforce one rule rather than two.
//
// An EMPTY outstanding list does NOT enable it. The request only exists because something is
// outstanding, so an empty list means the board sent something malformed — and the safe reading
// of "nothing to acknowledge" at a security desk is not "approve freely".
export const canApproveEntry = ({
  outstanding = [],
  acknowledged = [],
  reason = "",
  note = "",
} = {}) =>
  outstanding.length > 0 &&
  // EXACTLY the current set, which is what their 422 checks. Ticking every item is not enough
  // if the list grew underneath: `every` would still pass while an id was missing from the
  // request. Comparing both directions is what catches that before the server does.
  outstanding.every((item) => acknowledged.includes(item.id)) &&
  acknowledged.length === outstanding.length &&
  Boolean(reason) &&
  (!approvalReasonNeedsNote(reason) || Boolean(note.trim()));
